import { useState } from "react";
import { CAR_YEARS, WHEEL_SIZES, ORDER_SOURCES, copy } from "@wheels-crm/shared-types";
import type { OrderSource, ProductLine, WheelSize } from "@wheels-crm/shared-types";
import { FieldWrap, Input, Textarea } from "../ui/Input";
import { Select } from "../ui/Select";
import { Button } from "../ui/Button";

export type OrderFormValues = {
  clientName: string;
  phone: string;
  sizeSpec: WheelSize | "";
  carBrand: string;
  carYear: string; // "" or numeric string, dropdown value
  source: OrderSource;
  purchasePrice: string;
  amount: string;
  prepayment: string;
  extraSales: string;
  deliveryCost: string;
  orderComment: string;
  notes: string;
};

/** DD.MM: prefix used to default an empty operator-comment field (§6.6). */
export function todayNotesPrefix(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}: `;
}

export function emptyOrderFormValues(): OrderFormValues {
  return {
    clientName: "",
    phone: "",
    sizeSpec: "",
    carBrand: "",
    carYear: "",
    source: "phone",
    purchasePrice: "",
    amount: "",
    prepayment: "",
    extraSales: "",
    deliveryCost: "",
    orderComment: "",
    notes: todayNotesPrefix(),
  };
}

export function OrderForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  productLine,
}: {
  initial: OrderFormValues;
  submitLabel: string;
  onSubmit: (values: OrderFormValues) => Promise<void>;
  onCancel: () => void;
  /** Диски require a wheel size; рулі skip it and describe everything in the comment. */
  productLine: ProductLine;
}) {
  const [values, setValues] = useState<OrderFormValues>(initial);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function set<K extends keyof OrderFormValues>(key: K, value: OrderFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const sizeSpecMissing = productLine === "wheels" && !values.sizeSpec;
    if (!values.clientName.trim() || !values.phone.trim() || sizeSpecMissing) {
      setError(
        productLine === "wheels"
          ? "Заповніть обов'язкові поля: ім'я, телефон, розмір диска"
          : "Заповніть обов'язкові поля: ім'я, телефон",
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не вдалося зберегти");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card" style={{ maxWidth: 560 }}>
      <FieldWrap label="Ім'я клієнта" htmlFor="clientName">
        <Input
          id="clientName"
          required
          value={values.clientName}
          onChange={(e) => set("clientName", e.target.value)}
        />
      </FieldWrap>

      <FieldWrap label={copy.table.phone} htmlFor="phone">
        <Input id="phone" required value={values.phone} onChange={(e) => set("phone", e.target.value)} />
      </FieldWrap>

      {productLine === "wheels" && (
        <FieldWrap label={copy.field.wheelSize} htmlFor="sizeSpec">
          <Select
            id="sizeSpec"
            required
            value={values.sizeSpec}
            onChange={(e) => set("sizeSpec", e.target.value as WheelSize)}
          >
            <option value="" disabled>
              —
            </option>
            {WHEEL_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </FieldWrap>
      )}

      {productLine === "wheels" ? (
        <>
          <FieldWrap label={copy.field.carBrand} htmlFor="carBrand">
            <Input id="carBrand" value={values.carBrand} onChange={(e) => set("carBrand", e.target.value)} />
          </FieldWrap>

          <FieldWrap label={copy.field.carYear} htmlFor="carYear">
            <Select id="carYear" value={values.carYear} onChange={(e) => set("carYear", e.target.value)}>
              <option value=""></option>
              {CAR_YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </FieldWrap>

          <FieldWrap label={copy.field.source} htmlFor="source">
            <Select
              id="source"
              value={values.source}
              onChange={(e) => set("source", e.target.value as OrderSource)}
            >
              {ORDER_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {copy.source[s]}
                </option>
              ))}
            </Select>
          </FieldWrap>

          <FieldWrap label={copy.field.purchasePrice} htmlFor="purchasePrice">
            <Input
              id="purchasePrice"
              type="number"
              min="0"
              step="0.01"
              value={values.purchasePrice}
              onChange={(e) => set("purchasePrice", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label={copy.field.salePrice} htmlFor="amount">
            <Input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={values.amount}
              onChange={(e) => set("amount", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label={copy.field.prepayment} htmlFor="prepayment">
            <Input
              id="prepayment"
              type="number"
              min="0"
              step="0.01"
              value={values.prepayment}
              onChange={(e) => set("prepayment", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label={copy.field.extraSales} htmlFor="extraSales">
            <Input
              id="extraSales"
              type="number"
              min="0"
              step="0.01"
              value={values.extraSales}
              onChange={(e) => set("extraSales", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label={copy.field.deliveryCost} htmlFor="deliveryCost">
            <Input
              id="deliveryCost"
              type="number"
              min="0"
              step="0.01"
              value={values.deliveryCost}
              onChange={(e) => set("deliveryCost", e.target.value)}
            />
          </FieldWrap>
        </>
      ) : (
        <>
          <FieldWrap label={copy.field.carBrandYear} htmlFor="carBrand">
            <Input id="carBrand" value={values.carBrand} onChange={(e) => set("carBrand", e.target.value)} />
          </FieldWrap>

          <FieldWrap label={copy.field.orderValue} htmlFor="amount">
            <Input
              id="amount"
              type="number"
              min="0"
              step="0.01"
              value={values.amount}
              onChange={(e) => set("amount", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label={copy.field.costPrice} htmlFor="purchasePrice">
            <Input
              id="purchasePrice"
              type="number"
              min="0"
              step="0.01"
              value={values.purchasePrice}
              onChange={(e) => set("purchasePrice", e.target.value)}
            />
          </FieldWrap>

          <FieldWrap label={copy.field.orderComment} htmlFor="orderComment">
            <Textarea
              id="orderComment"
              rows={3}
              value={values.orderComment}
              onChange={(e) => set("orderComment", e.target.value)}
            />
          </FieldWrap>
        </>
      )}

      <FieldWrap label={copy.field.operatorComment} htmlFor="notes">
        <Textarea id="notes" rows={3} value={values.notes} onChange={(e) => set("notes", e.target.value)} />
      </FieldWrap>

      {error && <p style={{ color: "var(--color-danger)", fontSize: 13 }}>{error}</p>}

      <div className="modal-actions" style={{ justifyContent: "flex-start" }}>
        <Button type="submit" variant="primary" disabled={submitting}>
          {submitLabel}
        </Button>
        <Button type="button" onClick={onCancel}>
          {copy.cancel}
        </Button>
      </div>
    </form>
  );
}
